import { useContext, useEffect, useState } from "react";
import trans, { isRTL } from "../../../utils";
import { filtrationcontext } from "../../../context/Filtercontext";
import { useLazyGetIRHistoryQuery } from "../../../Redux/service/IRHistory/IRHistory";
import AntdTable from "../../antd/Table/Table";
import { useSelector } from "react-redux";

const IRHistoryTable = ({
  filters,
  currentPage,
  setCurrentPage,
  isShown,
  onDataChange,
}) => {
  const {
    setSort: SetSortFilter,
    searchQuery,
    setSearchQuery,
    limit,
    setLimit,
  } = useContext(filtrationcontext);

  const [sort, setSort] = useState({
    by: "",
    order: "",
  });

  const [trigger, { data, isLoading }] = useLazyGetIRHistoryQuery();
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.users.user);
  const userRole = user?.attributes?.role;

  const fieldMapping = {
    irNumber: "irNumber",
    type: "type",
    project: "project",
    discipline: "discipline",
    submittedDate: "submittedDate",
    reviewedDate: "reviewedDate",
    reviewResult: "reviewResult",
    deficiencies: "deficiencies",
    turnaroundTime: "turnaroundTime",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const formatTime = (milliseconds) => {
    if (!milliseconds) return "-";
    const totalMinutes = Math.floor(milliseconds / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (isRTL) {
      return `${hours}س ${minutes}د`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  const columns = [
    {
      title: trans("IR Number"),
      dataIndex: "ir_number",
      width: 150,
      fixed: !isRTL ? "left" : undefined,
      sorter: true,
      render: (irNumber) => (
        <span className="table-element-name ps-0">{irNumber || "-"}</span>
      ),
    },
    {
      title: trans("Type"),
      dataIndex: "type",
      width: 120,
      sorter: true,
      render: (type) => <span>{type || "-"}</span>,
    },
    {
      title: trans("Project"),
      dataIndex: "project",
      width: 180,
      sorter: true,
      render: (project) => <span>{project?.name || "-"}</span>,
    },
    {
      title: trans("Discipline"),
      dataIndex: "discipline",
      width: 150,
      sorter: true,
      render: (discipline) => <span>{discipline || "-"}</span>,
    },
    {
      title: trans("Submitted Date"),
      dataIndex: "submitted_at",
      width: 180,
      sorter: true,
      render: (submittedDate) => (
        <span className="table-element-date">{formatDate(submittedDate)}</span>
      ),
    },
    {
      title: trans("Reviewed Date"),
      dataIndex: "qa_review",
      width: 180,
      sorter: true,
      render: (qa_review) => (
        <span className="table-element-date">
          {formatDate(qa_review?.reviewed_at)}
        </span>
      ),
    },
    {
      title: trans("Review Result"),
      dataIndex: "qa_review",
      width: 150,
      sorter: true,
      render: (qa_review) => {
        const isApproved = qa_review?.result === "APPROVED";
        const result = qa_review?.result;
        return (
          <span
            style={{
              color: isApproved ? "#52c41a" : "#ff4d4f",
              fontWeight: 500,
            }}
          >
            {result ? trans(result.replace(/_/g, " ")) : "-"}
          </span>
        );
      },
    },
    {
      title: trans("Deficiencies"),
      dataIndex: "deficiencies",
      width: 150,
      render: (qa_review) => (
        <span>
          {qa_review?.deficiencies_count ? qa_review?.deficiencies_count : "-"}
        </span>
      ),
    },
    {
      title: trans("Turnaround Time"),
      dataIndex: "qa_turnaround_hours",
      width: 180,
      sorter: true,
      render: (turnaroundTime) => <span>{formatTime(turnaroundTime)}</span>,
    },
  ];

  useEffect(() => {
    if (isShown) {
      setLoading(true);
      trigger({
        historyType: "ir",
        role: userRole,
        type: filters.type,
        project: filters.project,
        discipline: filters.discipline,
        requestStatus: filters.requestStatus,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page: currentPage,
        pageSize: limit,
        sortBy: sort.by,
        sortOrder: sort.order,
        search: filters.search || searchQuery,
      }).finally(() => setLoading(false));
    }
  }, [
    isShown,
    filters,
    currentPage,
    limit,
    sort.by,
    sort.order,
    searchQuery,
    trigger,
  ]);

  useEffect(() => {
    if (data && onDataChange) {
      onDataChange({
        totalReviewed:
          data?.totalReviewed || data?.statistics?.totalReviewed || 0,
        approvedThisPeriod:
          data?.approvedThisPeriod || data?.statistics?.approvedThisPeriod || 0,
        rejectedThisPeriod:
          data?.rejectedThisPeriod || data?.statistics?.rejectedThisPeriod || 0,
        averageReviewTime:
          data?.averageReviewTime || data?.statistics?.averageReviewTime || 0,
      });
    }
  }, [data, onDataChange]);

  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      localStorage.setItem("page", 1);
      setSearchQuery("");
    };
  }, [SetSortFilter, setSearchQuery]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      const apiField = fieldMapping[sorter.field] || sorter.field;
      setSort({
        by: apiField,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({
        by: "",
        order: "",
      });
    }
  };

  return (
    <div className="bg-white-rounded-top mt-3 table-parent">
      <AntdTable
        columns={columns}
        dataSource={data?.data || []}
        loading={loading || isLoading}
        pageSize={limit}
        totalItems={data?.pagination?.total || 0}
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setCurrentPage(1);
        }}
        onTableChange={handleTableChange}
        scrollY={350}
      />
    </div>
  );
};

export default IRHistoryTable;
